import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
    TextField, Tooltip, Typography,
} from '@mui/material';
import { ontologyErrorMessage, scenarioVocabularyApi } from '../../api/ontologyApi';
import { conceptHref } from './ontologyFormat';
import { dsVar, ink } from '../../theme/tokens';

export interface TypologyOption {
    code: string;
    label: string;
    definition?: string | null;
}

interface ScenarioMapping {
    typologies: string[];
    rationale?: string;
    source?: string;
    proposedBy?: string | null;
    proposedAt?: string | null;
    approvedBy?: string | null;
    approvedAt?: string | null;
    effectiveFrom?: string | null;
}

interface Suggestion {
    typologies: string[];
    triggerFeature: string | null;
    source: 'binding' | 'alert_type' | 'rule_name';
    reason: string;
}

interface HistoryEvent {
    action: 'proposed' | 'approved' | 'discarded';
    by: string | null;
    at: string;
    typologies?: string[];
    note?: string | null;
}

export interface ScenarioRow {
    code: string;
    ruleName: string | null;
    triggerFeature: string | null;
    triggerFeatures: string[];
    caseHistoryAlerts: number;
    sampleAlerts: number;
    approved: ScenarioMapping | null;
    draft: ScenarioMapping | null;
    suggestion: Suggestion | null;
    history: HistoryEvent[];
}

type Filter = 'all' | 'unmapped' | 'pending' | 'approved';

const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'unmapped', label: 'Not mapped' },
    { key: 'pending', label: 'Awaiting approval' },
    { key: 'approved', label: 'Approved' }
];

const SOURCE_LABEL: Record<string, string> = {
    binding: 'from the trigger-feature binding',
    alert_type: 'from the alert-type mapping',
    rule_name: "from the rule's name",
    manual: 'entered by hand'
};

const labelSx = {
    fontSize: '0.6875rem', fontWeight: 600, color: dsVar('fg-muted'), textTransform: 'uppercase' as const,
    letterSpacing: '0.05em', mb: 0.5
};
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: dsVar('page'), color: dsVar('fg-body'), fontSize: '0.875rem',
        '& fieldset': { borderColor: dsVar('line') },
        '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') }
    },
    '& .MuiInputLabel-root': { color: dsVar('fg-muted'), fontSize: '0.8125rem' },
    '& .MuiFormHelperText-root': { color: dsVar('fg-muted'), fontSize: '0.75rem' }
};
const dialogPaper = { sx: { backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, backgroundImage: 'none' } };
const actionSx = { textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, minWidth: 0, px: 1.25 };

/** The signed-in user, as the control plane records them. Absent in a test or a private window. */
const currentUser = (): string | null => {
    try { return localStorage.getItem('user_email'); } catch { return null; }
};

const sameUser = (a?: string | null, b?: string | null) => Boolean(a && b && a.toLowerCase() === b.toLowerCase());

const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const statusOf = (row: ScenarioRow): Exclude<Filter, 'all'> =>
    row.draft ? 'pending' : row.approved ? 'approved' : 'unmapped';

const TypologyChips: React.FC<{ codes: string[]; names: Map<string, string>; tone?: 'accent' | 'muted' }> = ({ codes, names, tone = 'accent' }) => (
    <Box className="flex flex-wrap gap-1">
        {codes.map((code) => (
            <Chip
                key={code}
                size="small"
                component="a"
                href={conceptHref(code) ?? undefined}
                clickable
                label={names.get(code) ?? code}
                sx={{
                    height: 22, fontSize: '0.75rem', fontWeight: 600,
                    color: tone === 'accent' ? dsVar('accent-fg') : dsVar('fg-soft'),
                    backgroundColor: tone === 'accent' ? dsVar('accent', 0.1) : dsVar('raised'),
                    border: `1px solid ${tone === 'accent' ? dsVar('accent', 0.3) : dsVar('line')}`
                }}
            />
        ))}
    </Box>
);

/** Propose a mapping, or change the pending one. Whoever saves it becomes its proposer. */
const ProposeDialog: React.FC<{
    row: ScenarioRow | null;
    start: { typologies: string[]; triggerFeature: string | null; rationale: string; source: string } | null;
    typologies: TypologyOption[];
    onClose: () => void;
    onSaved: (message: string) => void;
}> = ({ row, start, typologies, onClose, onSaved }) => {
    const [chosen, setChosen] = useState<TypologyOption[]>([]);
    const [ruleName, setRuleName] = useState('');
    const [trigger, setTrigger] = useState('');
    const [rationale, setRationale] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!row || !start) return;
        const byCode = new Map(typologies.map((t) => [t.code, t]));
        setChosen(start.typologies.map((c) => byCode.get(c) ?? { code: c, label: c }));
        setRuleName(row.ruleName ?? '');
        setTrigger(start.triggerFeature ?? row.triggerFeature ?? '');
        setRationale(start.rationale);
        setError(null);
    }, [row, start, typologies]);

    if (!row || !start) return null;
    const canSave = chosen.length > 0 && rationale.trim().length > 0 && !saving;

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await scenarioVocabularyApi.saveDraft(row.code, {
                typologies: chosen.map((t) => t.code),
                rationale: rationale.trim(),
                ruleName: ruleName.trim() || undefined,
                triggerFeature: trigger.trim() || undefined,
                source: start.source
            });
            onSaved(`Proposed a mapping for ${row.code}. Someone else needs to approve it before runs use it.`);
        } catch (err) {
            setError(ontologyErrorMessage(err, 'Could not save the proposal.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={dialogPaper}>
            <DialogTitle sx={{ color: dsVar('fg'), fontSize: '1rem', fontWeight: 600 }}>
                {row.approved ? 'Propose a change to' : 'Propose typologies for'} {row.ruleName || row.code}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6, mb: 2 }}>
                    Which risks is <strong>{row.code}</strong> designed to detect? Benchmarks show these as the alert typology of the
                    cases this rule raises. {row.approved ? 'The approved mapping keeps running until someone approves this change.' : ''}
                </Typography>

                <Autocomplete
                    multiple
                    options={typologies}
                    value={chosen}
                    onChange={(_e, value) => setChosen(value)}
                    getOptionLabel={(t) => t.label}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    renderOption={(props, t) => (
                        <li {...props} key={t.code}>
                            <Box>
                                <Typography sx={{ fontSize: '0.875rem' }}>{t.label}</Typography>
                                {t.definition && <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>{t.definition}</Typography>}
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Typologies" size="small" helperText="List the main typology first." sx={fieldSx} />
                    )}
                    sx={{ mb: 2 }}
                />

                <Box className="grid gap-2" sx={{ gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2 }}>
                    <TextField label="Rule name" size="small" value={ruleName} onChange={(e) => setRuleName(e.target.value)} sx={fieldSx} />
                    <TextField
                        select={row.triggerFeatures.length > 0}
                        label="Trigger feature"
                        size="small"
                        value={trigger}
                        onChange={(e) => setTrigger(e.target.value)}
                        helperText="What the rule fires on."
                        sx={fieldSx}
                    >
                        {row.triggerFeatures.map((f) => <MenuItem key={f} value={f} sx={{ fontSize: '0.875rem' }}>{f}</MenuItem>)}
                    </TextField>
                </Box>

                <TextField
                    label="Why"
                    required
                    multiline
                    minRows={3}
                    fullWidth
                    size="small"
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    helperText="The reviewer approves your reasoning, not just the codes."
                    sx={fieldSx}
                />
                {error && <Typography sx={{ mt: 1.5, fontSize: '0.8125rem', color: ink('#F87171') }}>{error}</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...actionSx, color: dsVar('fg-2') }}>Cancel</Button>
                <Button
                    onClick={save}
                    disabled={!canSave}
                    startIcon={saving ? <CircularProgress size={14} /> : undefined}
                    sx={{ ...actionSx, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' }, '&.Mui-disabled': { opacity: 0.5, color: dsVar('on-accent') } }}
                >
                    Save proposal
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Put the pending mapping in force: what is in force now, what replaces it, and why. */
const ApproveScenarioDialog: React.FC<{
    row: ScenarioRow | null;
    names: Map<string, string>;
    onClose: () => void;
    onApproved: (message: string) => void;
}> = ({ row, names, onClose, onApproved }) => {
    const [effectiveFrom, setEffectiveFrom] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { setEffectiveFrom(''); setNote(''); setError(null); }, [row]);
    if (!row || !row.draft) return null;

    const approve = async () => {
        setSaving(true);
        setError(null);
        try {
            await scenarioVocabularyApi.approve(row.code, {
                ...(effectiveFrom ? { effectiveFrom } : {}),
                ...(note.trim() ? { note: note.trim() } : {})
            });
            onApproved(`Approved the mapping for ${row.code}. Benchmark runs from now on use it.`);
        } catch (err) {
            setError(ontologyErrorMessage(err, 'Could not approve the mapping.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={dialogPaper}>
            <DialogTitle sx={{ color: dsVar('fg'), fontSize: '1rem', fontWeight: 600 }}>
                Approve the mapping for {row.ruleName || row.code}?
            </DialogTitle>
            <DialogContent>
                <Box className="grid gap-3" sx={{ gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 2.5 }}>
                    <Box>
                        <Typography sx={labelSx}>Currently in force</Typography>
                        {row.approved ? <TypologyChips codes={row.approved.typologies} names={names} tone="muted" />
                            : <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>Nothing yet</Typography>}
                    </Box>
                    <Box>
                        <Typography sx={labelSx}>Proposed</Typography>
                        <TypologyChips codes={row.draft.typologies} names={names} />
                    </Box>
                </Box>
                <Typography sx={labelSx}>Why, from {row.draft.proposedBy ?? 'the proposer'}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6, mb: 2.5 }}>{row.draft.rationale}</Typography>

                <Box className="grid gap-2" sx={{ gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 1.5 }}>
                    <TextField
                        type="date" size="small" label="Effective from" InputLabelProps={{ shrink: true }}
                        value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
                        helperText="Blank: from now." sx={fieldSx}
                    />
                    <TextField size="small" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} sx={fieldSx} />
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), lineHeight: 1.6 }}>
                    You will be recorded as the approver. Runs scored after this date show these typologies for {row.code}&apos;s cases,
                    and its trigger feature is checked against the first one.
                </Typography>
                {error && <Typography sx={{ mt: 1.5, fontSize: '0.8125rem', color: ink('#F87171') }}>{error}</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ ...actionSx, color: dsVar('fg-2') }}>Cancel</Button>
                <Button
                    onClick={approve}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} /> : undefined}
                    sx={{ ...actionSx, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' } }}
                >
                    Approve
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const HistoryList: React.FC<{ events: HistoryEvent[]; names: Map<string, string> }> = ({ events, names }) => (
    <Box sx={{ mt: 1, pl: 1.5, borderLeft: `2px solid ${dsVar('line')}` }}>
        {[...events].reverse().map((e, i) => (
            <Typography key={`${e.at}-${i}`} sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), lineHeight: 1.7 }}>
                {formatDate(e.at)} · {e.by ?? 'unknown'} {e.action}
                {e.typologies?.length ? ` ${e.typologies.map((t) => names.get(t) ?? t).join(', ')}` : ''}
                {e.note ? ` — ${e.note}` : ''}
            </Typography>
        ))}
    </Box>
);

const ScenarioRowView: React.FC<{
    row: ScenarioRow;
    names: Map<string, string>;
    focused: boolean;
    user: string | null;
    onPropose: (row: ScenarioRow, from: 'blank' | 'suggestion' | 'draft' | 'approved') => void;
    onApprove: (row: ScenarioRow) => void;
    onDiscard: (row: ScenarioRow) => void;
}> = ({ row, names, focused, user, onPropose, onApprove, onDiscard }) => {
    const [showHistory, setShowHistory] = useState(false);
    const ownDraft = row.draft && sameUser(row.draft.proposedBy, user);
    const alerts = [
        row.caseHistoryAlerts ? `${row.caseHistoryAlerts.toLocaleString()} in case history` : null,
        // Often the same alerts as case history, loaded into a benchmark dataset: named for
        // where they sit, so the two counts are not read as one total.
        row.sampleAlerts ? `${row.sampleAlerts.toLocaleString()} in benchmark datasets` : null
    ].filter(Boolean).join(' · ');

    return (
        <Box
            data-scenario={row.code}
            sx={{
                display: 'grid', gap: { xs: 1, md: 2 }, py: 1.75, px: 1.5, borderTop: `1px solid ${dsVar('line')}`,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(14rem, 1.3fr) minmax(12rem, 1fr) minmax(14rem, 1.3fr) auto' },
                backgroundColor: focused ? dsVar('raised') : 'transparent',
                boxShadow: focused ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none'
            }}
        >
            <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: dsVar('fg-body') }}>{row.ruleName || row.code}</Typography>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                    {row.code}{row.triggerFeature ? ` · fires on ${row.triggerFeature}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.25 }}>{alerts || 'No alerts loaded'}</Typography>
                {row.history.length > 0 && (
                    <Button onClick={() => setShowHistory((v) => !v)} sx={{ p: 0, mt: 0.5, minWidth: 0, textTransform: 'none', fontSize: '0.75rem', color: dsVar('fg-2'), '&:hover': { backgroundColor: 'transparent', color: dsVar('accent-fg') } }}>
                        {showHistory ? 'Hide history' : `History (${row.history.length})`}
                    </Button>
                )}
                {showHistory && <HistoryList events={row.history} names={names} />}
            </Box>

            <Box>
                <Typography sx={labelSx}>In force</Typography>
                {row.approved ? (
                    <>
                        <TypologyChips codes={row.approved.typologies} names={names} />
                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.5 }}>
                            Approved by {row.approved.approvedBy ?? 'unknown'}{row.approved.approvedAt ? `, ${formatDate(row.approved.approvedAt)}` : ''}
                        </Typography>
                    </>
                ) : (
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ink('#FBBF24') }}>Not mapped</Typography>
                )}
            </Box>

            <Box>
                {row.draft ? (
                    <>
                        <Typography sx={labelSx}>Awaiting approval</Typography>
                        <TypologyChips codes={row.draft.typologies} names={names} tone="muted" />
                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.5 }}>
                            Proposed by {ownDraft ? 'you' : row.draft.proposedBy ?? 'unknown'}{row.draft.proposedAt ? `, ${formatDate(row.draft.proposedAt)}` : ''}
                        </Typography>
                        {row.draft.rationale && <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.25 }}>{row.draft.rationale}</Typography>}
                    </>
                ) : row.suggestion ? (
                    <>
                        <Typography sx={labelSx}>Suggested {SOURCE_LABEL[row.suggestion.source] ?? ''}</Typography>
                        <TypologyChips codes={row.suggestion.typologies} names={names} tone="muted" />
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.25 }}>{row.suggestion.reason}</Typography>
                    </>
                ) : null}
            </Box>

            <Box className="flex flex-wrap items-start gap-1" sx={{ justifyContent: { md: 'flex-end' } }}>
                {row.draft ? (
                    <>
                        <Tooltip arrow title={ownDraft ? 'You proposed this, so someone else must approve it.' : ''}>
                            <span>
                                <Button
                                    onClick={() => onApprove(row)}
                                    disabled={Boolean(ownDraft)}
                                    sx={{ ...actionSx, backgroundColor: dsVar('accent'), color: dsVar('on-accent'), '&:hover': { backgroundColor: '#F5D149' }, '&.Mui-disabled': { opacity: 0.45, color: dsVar('on-accent'), backgroundColor: dsVar('accent') } }}
                                >
                                    Approve
                                </Button>
                            </span>
                        </Tooltip>
                        <Button onClick={() => onPropose(row, 'draft')} sx={{ ...actionSx, color: dsVar('fg-2') }}>Edit</Button>
                        <Button onClick={() => onDiscard(row)} sx={{ ...actionSx, color: dsVar('fg-2') }}>Discard</Button>
                    </>
                ) : row.suggestion ? (
                    <>
                        <Button onClick={() => onPropose(row, 'suggestion')} sx={{ ...actionSx, color: dsVar('accent-fg'), border: `1px solid ${dsVar('accent', 0.35)}` }}>Use suggestion</Button>
                        <Button onClick={() => onPropose(row, 'blank')} sx={{ ...actionSx, color: dsVar('fg-2') }}>Propose</Button>
                    </>
                ) : (
                    <Button onClick={() => onPropose(row, row.approved ? 'approved' : 'blank')} sx={{ ...actionSx, color: dsVar('accent-fg'), border: `1px solid ${dsVar('accent', 0.35)}` }}>
                        {row.approved ? 'Propose change' : 'Propose'}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

/**
 * Ontology → Your vocabulary: the institution's detection scenarios, and the typologies
 * each one is approved as detecting.
 *
 * A scenario is the institution's own rule code, not a term of the library: this is where
 * it is translated into the typologies the library, the agents and every benchmark reason
 * in. The translation is a business judgement, so it is the analyst's to make and keep
 * current — proposed by one person, approved by another, with the history kept.
 */
const ScenarioVocabularyBrowser: React.FC<{ focusScenario?: string | null }> = ({ focusScenario }) => {
    const [rows, setRows] = useState<ScenarioRow[] | null>(null);
    const [typologies, setTypologies] = useState<TypologyOption[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [filter, setFilter] = useState<Filter>('all');
    const [attempt, setAttempt] = useState(0);
    const [proposing, setProposing] = useState<{ row: ScenarioRow; start: { typologies: string[]; triggerFeature: string | null; rationale: string; source: string } } | null>(null);
    const [approving, setApproving] = useState<ScenarioRow | null>(null);
    const user = useMemo(currentUser, []);

    useEffect(() => {
        const controller = new AbortController();
        setError(null);
        scenarioVocabularyApi.list({ signal: controller.signal })
            .then((res: any) => {
                const body = res?.data ?? res ?? {};
                setRows(body.scenarios ?? []);
                setTypologies(body.typologies ?? []);
            })
            .catch((err: any) => {
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                setError(ontologyErrorMessage(err, 'Could not load your detection scenarios.'));
            });
        return () => controller.abort();
    }, [attempt]);

    useEffect(() => {
        if (!focusScenario || !rows) return;
        const el = document.querySelector(`[data-scenario="${focusScenario}"]`) as HTMLElement | null;
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, [focusScenario, rows]);

    const names = useMemo(() => new Map(typologies.map((t) => [t.code, t.label])), [typologies]);
    const counts = useMemo(() => {
        const c = { all: 0, unmapped: 0, pending: 0, approved: 0 } as Record<Filter, number>;
        for (const row of rows ?? []) { c.all += 1; c[statusOf(row)] += 1; }
        return c;
    }, [rows]);
    const shown = useMemo(() => (rows ?? []).filter((r) => filter === 'all' || statusOf(r) === filter), [rows, filter]);

    const refresh = useCallback((message: string) => {
        setNotice(message);
        setProposing(null);
        setApproving(null);
        setAttempt((n) => n + 1);
    }, []);

    const propose = (row: ScenarioRow, from: 'blank' | 'suggestion' | 'draft' | 'approved') => {
        const base = from === 'suggestion' ? row.suggestion : from === 'draft' ? row.draft : from === 'approved' ? row.approved : null;
        setProposing({
            row,
            start: {
                typologies: base?.typologies ?? [],
                triggerFeature: (from === 'suggestion' && row.suggestion?.triggerFeature) || row.triggerFeature,
                rationale: from === 'suggestion' ? row.suggestion?.reason ?? '' : from === 'draft' ? row.draft?.rationale ?? '' : '',
                source: from === 'suggestion' ? row.suggestion?.source ?? 'manual' : 'manual'
            }
        });
    };

    const discard = async (row: ScenarioRow) => {
        try {
            await scenarioVocabularyApi.discardDraft(row.code);
            refresh(`Discarded the proposal for ${row.code}.${row.approved ? ' The approved mapping keeps running.' : ''}`);
        } catch (err) {
            setNotice(ontologyErrorMessage(err, 'Could not discard the proposal.'));
        }
    };

    if (error) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), mb: 1.5 }}>{error}</Typography>
                <Button onClick={() => setAttempt((n) => n + 1)} sx={{ ...actionSx, color: dsVar('accent-fg') }}>Try again</Button>
            </Box>
        );
    }
    if (!rows) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} /></Box>;

    return (
        <Box>
            <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), lineHeight: 1.7, maxWidth: '52rem', mb: 2 }}>
                Your monitoring system&apos;s detection scenarios, and the typologies each one is designed to detect. Benchmarks use these to
                show the alert typology of each case. A mapping comes into force when someone other than the person who proposed it approves it.
            </Typography>

            <Box className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Filter scenarios">
                {FILTERS.map((f) => {
                    const active = filter === f.key;
                    return (
                        <Chip
                            key={f.key}
                            label={`${f.label} · ${counts[f.key]}`}
                            onClick={() => setFilter(f.key)}
                            aria-pressed={active}
                            sx={{
                                fontSize: '0.75rem', fontWeight: 600,
                                color: active ? dsVar('on-accent') : dsVar('fg-2'),
                                backgroundColor: active ? dsVar('accent') : dsVar('raised'),
                                border: `1px solid ${active ? dsVar('accent') : dsVar('line')}`,
                                '&:hover': { backgroundColor: active ? dsVar('accent') : dsVar('surface') }
                            }}
                        />
                    );
                })}
            </Box>

            {notice && (
                <Typography role="status" sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), mb: 2, p: 1.25, borderRadius: '6px', backgroundColor: dsVar('raised'), border: `1px solid ${dsVar('line')}` }}>
                    {notice}
                </Typography>
            )}

            <Box sx={{ border: `1px solid ${dsVar('line')}`, borderRadius: '8px', backgroundColor: dsVar('surface') }}>
                {shown.length === 0 ? (
                    <Typography sx={{ p: 5, textAlign: 'center', fontSize: '0.875rem', color: dsVar('fg-muted') }}>
                        {rows.length === 0
                            ? 'No detection scenarios yet. They appear here once case history or a benchmark sample with scenario codes is loaded.'
                            : 'No scenarios match this filter.'}
                    </Typography>
                ) : shown.map((row) => (
                    <ScenarioRowView
                        key={row.code}
                        row={row}
                        names={names}
                        focused={row.code === focusScenario}
                        user={user}
                        onPropose={propose}
                        onApprove={setApproving}
                        onDiscard={discard}
                    />
                ))}
            </Box>

            <ProposeDialog
                row={proposing?.row ?? null}
                start={proposing?.start ?? null}
                typologies={typologies}
                onClose={() => setProposing(null)}
                onSaved={refresh}
            />
            <ApproveScenarioDialog row={approving} names={names} onClose={() => setApproving(null)} onApproved={refresh} />
        </Box>
    );
};

export default ScenarioVocabularyBrowser;
